import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useApiData } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Minus, Users, Calendar, Target, AlertTriangle,
  RefreshCw, Download, Activity, ArrowUpRight, ArrowDownRight, ChevronRight,
  Building2, UserPlus, UserMinus, Clock, CheckCircle2, Info, BarChart3, LineChart,
  Heart, Shield, Zap, FileText, AlertCircle
} from 'lucide-react';
import {
  LineChart as RechartsLine, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, Area, AreaChart, BarChart, Bar, ReferenceLine,
  ComposedChart
} from 'recharts';
import { PageHeader } from '@/components/ModernUI';
import PDFExportButton from '@/components/common/PDFExportButton';
import DataFixToolbar from '@/components/common/DataFixToolbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trend icon helper
const TrendIcon = ({ direction, className = "h-4 w-4" }) => {
  if (direction === 'increasing') return <TrendingUp className={`${className} text-emerald-500`} />;
  if (direction === 'decreasing') return <TrendingDown className={`${className} text-rose-500`} />;
  return <Minus className={`${className} text-amber-500`} />;
};

// Confidence badge with futuristic styling
const ConfidenceBadge = ({ level, score }) => {
  const config = {
    High: { gradient: 'from-emerald-400 to-teal-500', glow: 'rgba(52, 211, 153, 0.5)' },
    Moderate: { gradient: 'from-amber-400 to-orange-500', glow: 'rgba(251, 191, 36, 0.5)' },
    Low: { gradient: 'from-rose-400 to-red-500', glow: 'rgba(251, 113, 133, 0.5)' },
    None: { gradient: 'from-slate-400 to-slate-500', glow: 'rgba(148, 163, 184, 0.5)' }
  };
  
  const c = config[level] || config.None;
  
  return (
    <span 
      className={`px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${c.gradient}`}
      style={{ boxShadow: `0 0 15px ${c.glow}` }}
    >
      {level} Confidence {score ? `(${score}%)` : ''}
    </span>
  );
};

// Futuristic Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "cyan" }) => {
  const colorConfig = {
    cyan: { hex: '#22d3ee', glow: 'rgba(34, 211, 238, 0.3)' },
    emerald: { hex: '#34d399', glow: 'rgba(52, 211, 153, 0.3)' },
    amber: { hex: '#fbbf24', glow: 'rgba(251, 191, 36, 0.3)' },
    rose: { hex: '#fb7185', glow: 'rgba(251, 113, 133, 0.3)' },
    blue: { hex: '#3b82f6', glow: 'rgba(59, 130, 246, 0.3)' },
    violet: { hex: '#a78bfa', glow: 'rgba(167, 139, 250, 0.3)' }
  };
  
  const c = colorConfig[color] || colorConfig.cyan;
  
  return (
    <Card 
      className="relative overflow-hidden bg-slate-900/80 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:scale-[1.02] group"
      style={{ 
        borderColor: c.hex + '40',
        boxShadow: `0 0 25px ${c.glow}`
      }}
    >
      {/* Animated background */}
      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity" style={{ background: `linear-gradient(135deg, ${c.hex}30 0%, transparent 50%)` }} />
      
      <CardContent className="p-6 relative z-10">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-400 font-bold uppercase tracking-wide">{title}</p>
            <p className="text-4xl font-black mt-1 text-white" style={{ textShadow: `0 0 20px ${c.glow}` }}>{value}</p>
            {subtitle && <p className="text-sm text-slate-500 mt-1 font-medium">{subtitle}</p>}
          </div>
          <div 
            className="h-14 w-14 rounded-2xl flex items-center justify-center backdrop-blur-sm"
            style={{ 
              backgroundColor: c.hex + '15',
              boxShadow: `0 0 15px ${c.glow}`
            }}
          >
            <Icon className="h-7 w-7" style={{ color: c.hex, filter: `drop-shadow(0 0 8px ${c.hex})` }} />
          </div>
        </div>
        {trend && (
          <div className={`flex items-center gap-2 mt-3 font-bold ${
            trend.direction === 'up' ? 'text-emerald-400' : trend.direction === 'down' ? 'text-rose-400' : 'text-amber-400'
          }`}>
            {trend.direction === 'up' ? (
              <ArrowUpRight className="h-4 w-4" />
            ) : trend.direction === 'down' ? (
              <ArrowDownRight className="h-4 w-4" />
            ) : (
              <Minus className="h-4 w-4" />
            )}
            <span className="text-sm">{trend.value}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function CensusPredictions() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forecastWeeks, setForecastWeeks] = useState('12');
  const [forecast, setForecast] = useState(null);
  const [staffingData, setStaffingData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  const fetchForecast = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all data in parallel
      const [forecastRes, staffingRes, healthRes] = await Promise.all([
        fetch(`${API_URL}/api/advanced-analytics/census-forecast?weeks=${forecastWeeks}`, { headers }),
        fetch(`${API_URL}/api/strategic-plan/staffing-recommendations?weeks=${forecastWeeks}`, { headers }),
        fetch(`${API_URL}/api/strategic-plan/agency-health?weeks=${forecastWeeks}`, { headers })
      ]);
      
      if (forecastRes.ok) {
        const data = await forecastRes.json();
        setForecast(data);
      }
      
      if (staffingRes.ok) {
        const data = await staffingRes.json();
        setStaffingData(data);
      }
      
      if (healthRes.ok) {
        const data = await healthRes.json();
        setHealthData(data);
      }
    } catch (error) {
      toast.error('Error loading predictions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, forecastWeeks]);
  
  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);
  
  // Prepare chart data with census and ADC
  const chartData = forecast?.weekly_forecasts?.map(f => ({
    week: `Week ${f.week}`,
    date: new Date(f.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    adc: f.projected_adc,
    adcLow: f.adc_range?.low,
    adcHigh: f.adc_range?.high,
    census: f.projected_census,
    censusLow: f.census_range?.low,
    censusHigh: f.census_range?.high,
    admissions: f.projected_admissions,
    discharges: f.projected_discharges,
    deaths: f.exit_breakdown?.deaths || 0,
    liveDischarges: f.exit_breakdown?.live_discharges || 0,
    netChange: f.net_change,
    confidence: f.confidence === 'High' ? 100 : f.confidence === 'Moderate' ? 70 : 40
  })) || [];
  
  // Scenario chart data for ADC
  const scenarioData = forecast?.scenarios ? [
    { name: 'Pessimistic', adc: forecast.scenarios.pessimistic.end_adc, probability: forecast.scenarios.pessimistic.probability },
    { name: 'Expected', adc: forecast.scenarios.baseline.end_adc, probability: forecast.scenarios.baseline.probability },
    { name: 'Optimistic', adc: forecast.scenarios.optimistic.end_adc, probability: forecast.scenarios.optimistic.probability }
  ] : [];
  
  // Census scenario data
  const censusScenarioData = forecast?.census_scenarios ? [
    { name: 'Pessimistic', census: forecast.census_scenarios.pessimistic.end_census, probability: forecast.census_scenarios.pessimistic.probability },
    { name: 'Expected', census: forecast.census_scenarios.baseline.end_census, probability: forecast.census_scenarios.baseline.probability },
    { name: 'Optimistic', census: forecast.census_scenarios.optimistic.end_census, probability: forecast.census_scenarios.optimistic.probability }
  ] : [];
  
  // Export forecast with census data
  const handleExport = () => {
    if (!forecast) return;
    
    const csvContent = [
      ['Week', 'Date', 'Projected Census', 'Census Low', 'Census High', 'Projected ADC', 'ADC Low', 'ADC High', 'Admissions', 'Total Exits', 'Deaths', 'Live Discharges', 'Net Change', 'Confidence'],
      ...forecast.weekly_forecasts.map(f => [
        f.week, f.date, 
        f.projected_census, f.census_range?.low, f.census_range?.high,
        f.projected_adc, f.adc_range?.low, f.adc_range?.high,
        f.projected_admissions, f.projected_discharges, 
        f.exit_breakdown?.deaths || 0, f.exit_breakdown?.live_discharges || 0,
        f.net_change, f.confidence
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `census_predictions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Forecast exported');
  };
  
  if (loading) {
    return (
      <div className="space-y-6" data-testid="census-predictions-loading">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }
  
  if (forecast?.error === 'insufficient_data') {
    return (
      <div className="space-y-6">
        <PageHeader 
          icon={LineChart}
          title="Census Predictions"
          subtitle="Predictive analytics for weekly census forecasting"
        />
        <Card className="p-12 text-center">
          <AlertTriangle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
          <h3 className="text-xl font-semibold mb-2">Insufficient Data</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {forecast.message || 'At least 4 weeks of census data is required to generate accurate predictions.'}
          </p>
          <Button className="mt-6" onClick={() => window.location.href = '/admissions-discharges'}>
            Enter Census Data
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="census-predictions-page">
      {/* Futuristic Neon Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 animate-gradient-x" />
        
        {/* Mesh overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
                <LineChart className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
              </div>
              <span className="drop-shadow-lg" style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>
                Census Predictions
              </span>
            </h1>
            <p className="text-white/80 mt-3 text-lg font-medium max-w-xl">
              AI-powered weekly census forecasting with trend analysis
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={forecastWeeks} onValueChange={setForecastWeeks}>
              <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white backdrop-blur-sm font-bold" data-testid="forecast-weeks-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="4">4 Weeks</SelectItem>
                <SelectItem value="8">8 Weeks</SelectItem>
                <SelectItem value="12">12 Weeks</SelectItem>
                <SelectItem value="16">16 Weeks</SelectItem>
                <SelectItem value="26">26 Weeks</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="secondary" 
              onClick={() => fetchForecast(true)}
              disabled={refreshing}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold"
              data-testid="refresh-forecast-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="secondary" onClick={handleExport} className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-bold" data-testid="export-forecast-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <PDFExportButton 
              title="Census Predictions Report"
              filename="census-predictions-report"
              contentId="census-predictions-page"
              variant="secondary"
            />
          </div>
        </div>
      </div>
      
      {/* Forecast Period & Confidence */}
      <div className="flex flex-wrap items-center gap-4 px-2">
        <div className="flex items-center gap-2 text-sm text-slate-400 font-medium">
          <Calendar className="h-4 w-4 text-cyan-400" />
          <span>
            {forecast?.forecast_period?.start} to {forecast?.forecast_period?.end}
          </span>
        </div>
        <ConfidenceBadge 
          level={forecast?.confidence?.level} 
          score={forecast?.confidence?.overall_score} 
        />
        {forecast?.confidence?.factors?.map((factor, i) => (
          <span key={i} className="px-2 py-1 text-xs font-medium text-slate-400 bg-slate-800/50 rounded-lg border border-slate-700">
            {factor}
          </span>
        ))}
      </div>
      
      {/* Data Fix Toolbar */}
      <DataFixToolbar 
        onDataFixed={() => fetchForecast(true)} 
        variant="compact"
      />
      
      {/* Key Metrics - Census AND ADC */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Current Census"
          value={forecast?.current_state?.census || forecast?.current_state?.adc || 0}
          subtitle="Actual Patient Count"
          icon={Users}
          color="cyan"
        />
        <StatCard
          title={`Projected Census (${forecastWeeks}wk)`}
          value={forecast?.summary?.end_census || 0}
          subtitle={`${forecast?.summary?.census_growth_pct >= 0 ? '+' : ''}${forecast?.summary?.census_growth_pct || 0}% growth`}
          icon={Target}
          trend={{
            direction: forecast?.summary?.census_growth_pct > 0 ? 'up' : forecast?.summary?.census_growth_pct < 0 ? 'down' : 'stable',
            value: `${forecast?.summary?.net_growth > 0 ? '+' : ''}${forecast?.summary?.net_growth || 0} patients`
          }}
          color="emerald"
        />
        <StatCard
          title="Projected Admissions"
          value={forecast?.summary?.total_admissions || 0}
          subtitle={`~${Math.round((forecast?.summary?.total_admissions || 0) / parseInt(forecastWeeks))} per week`}
          icon={UserPlus}
          trend={{
            direction: forecast?.trends?.admissions?.direction === 'increasing' ? 'up' : 
                      forecast?.trends?.admissions?.direction === 'decreasing' ? 'down' : 'stable',
            value: `${forecast?.trends?.admissions?.change_pct >= 0 ? '+' : ''}${forecast?.trends?.admissions?.change_pct || 0}% trend`
          }}
          color="blue"
        />
        <StatCard
          title="Projected Exits"
          value={forecast?.summary?.total_discharges || 0}
          subtitle={`Deaths: ${forecast?.summary?.total_deaths || 0}, Discharges: ${forecast?.summary?.total_live_discharges || 0}`}
          icon={UserMinus}
          color="amber"
        />
        <StatCard
          title="Net Census Growth"
          value={`${forecast?.summary?.net_growth > 0 ? '+' : ''}${forecast?.summary?.net_growth || 0}`}
          subtitle="Over forecast period"
          icon={Activity}
          color={forecast?.summary?.net_growth >= 0 ? 'emerald' : 'rose'}
        />
      </div>
      
      {/* Historical Averages Reference */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="p-3 bg-muted/30">
          <p className="text-xs text-muted-foreground">Avg Admissions/wk</p>
          <p className="text-lg font-semibold">{forecast?.historical_averages?.avg_admissions || 0}</p>
        </Card>
        <Card className="p-3 bg-muted/30">
          <p className="text-xs text-muted-foreground">Avg Total Exits/wk</p>
          <p className="text-lg font-semibold">{forecast?.historical_averages?.avg_discharges || 0}</p>
        </Card>
        <Card className="p-3 bg-muted/30">
          <p className="text-xs text-muted-foreground">Avg Deaths/wk</p>
          <p className="text-lg font-semibold">{forecast?.historical_averages?.avg_deaths || 0}</p>
        </Card>
        <Card className="p-3 bg-muted/30">
          <p className="text-xs text-muted-foreground">Avg Census</p>
          <p className="text-lg font-semibold">{forecast?.historical_averages?.avg_census || 0}</p>
        </Card>
        <Card className="p-3 bg-muted/30">
          <p className="text-xs text-muted-foreground">Avg ADC</p>
          <p className="text-lg font-semibold">{forecast?.historical_averages?.avg_adc || 0}</p>
        </Card>
        <Card className="p-3 bg-muted/30">
          <p className="text-xs text-muted-foreground">Avg Net Change</p>
          <p className="text-lg font-semibold">{forecast?.historical_averages?.avg_net_change > 0 ? '+' : ''}{forecast?.historical_averages?.avg_net_change || 0}</p>
        </Card>
      </div>
      
      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="weekly" className="gap-2">
            <Calendar className="h-4 w-4" />
            Weekly Forecast
          </TabsTrigger>
          <TabsTrigger value="scenarios" className="gap-2">
            <LineChart className="h-4 w-4" />
            Scenarios
          </TabsTrigger>
          <TabsTrigger value="staffing" className="gap-2">
            <Users className="h-4 w-4" />
            Staffing Impact
          </TabsTrigger>
          <TabsTrigger value="health" className="gap-2">
            <Heart className="h-4 w-4" />
            Agency Health
          </TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChart className="h-5 w-5 text-[#00b0ab]" />
                  Census Projection with Confidence Interval
                </CardTitle>
                <CardDescription>
                  Projected ADC with upper and lower bounds
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="adcGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00b0ab" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#00b0ab" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="rangeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00b0ab" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#00b0ab" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} domain={['dataMin - 10', 'dataMax + 10']} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [
                          typeof value === 'number' ? value.toFixed(1) : value,
                          name === 'adc' ? 'Projected ADC' : 
                          name === 'adcLow' ? 'Lower Bound' :
                          name === 'adcHigh' ? 'Upper Bound' : name
                        ]}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="adcHigh" 
                        stroke="transparent"
                        fill="url(#rangeGradient)"
                        name="Upper Bound"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="adcLow" 
                        stroke="transparent"
                        fill="white"
                        name="Lower Bound"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="adc" 
                        stroke="#00b0ab"
                        strokeWidth={3}
                        fill="url(#adcGradient)"
                        name="Projected ADC"
                      />
                      <ReferenceLine 
                        y={forecast?.current_state?.adc} 
                        stroke="#6b7280" 
                        strokeDasharray="5 5"
                        label={{ value: 'Current', position: 'right', fontSize: 10 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Trends Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#00b0ab]" />
                  Trend Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* ADC Trend */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">ADC Trend</span>
                    <TrendIcon direction={forecast?.trends?.adc?.direction} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">
                      {forecast?.trends?.adc?.change_pct >= 0 ? '+' : ''}{forecast?.trends?.adc?.change_pct}%
                    </span>
                    <Badge variant="outline" className="text-xs capitalize">
                      {forecast?.trends?.adc?.direction}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Slope: {forecast?.trends?.adc?.slope} patients/week
                  </p>
                </div>
                
                {/* Admissions Trend */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Admissions Trend</span>
                    <TrendIcon direction={forecast?.trends?.admissions?.direction} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">
                      {forecast?.trends?.admissions?.change_pct >= 0 ? '+' : ''}{forecast?.trends?.admissions?.change_pct}%
                    </span>
                    <Badge variant="outline" className="text-xs capitalize">
                      {forecast?.trends?.admissions?.direction}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ~{forecast?.current_state?.weekly_admissions?.toFixed(1)} admissions/week
                  </p>
                </div>
                
                {/* Discharges Trend */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Discharges Trend</span>
                    <TrendIcon direction={forecast?.trends?.discharges?.direction} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">
                      {forecast?.trends?.discharges?.change_pct >= 0 ? '+' : ''}{forecast?.trends?.discharges?.change_pct}%
                    </span>
                    <Badge variant="outline" className="text-xs capitalize">
                      {forecast?.trends?.discharges?.direction}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ~{forecast?.current_state?.weekly_discharges?.toFixed(1)} discharges/week
                  </p>
                </div>
                
                {/* Net Change */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Net Weekly Change</span>
                  </div>
                  <span className={`text-2xl font-bold ${
                    forecast?.current_state?.net_weekly_change >= 0 ? 'text-emerald-600' : 'text-rose-600'
                  }`}>
                    {forecast?.current_state?.net_weekly_change >= 0 ? '+' : ''}{forecast?.current_state?.net_weekly_change}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    patients per week
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Admissions vs Discharges Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Admissions vs Discharges Forecast</CardTitle>
              <CardDescription>Weekly projected patient flow</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="admissions" fill="#00b0ab" name="Admissions" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="discharges" fill="#f97316" name="Discharges" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="netChange" stroke="#10b981" strokeWidth={2} name="Net Change" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Milestones */}
          {forecast?.milestones?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#00b0ab]" />
                  Projected Milestones
                </CardTitle>
                <CardDescription>When key ADC thresholds will be reached</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {forecast.milestones.map((m, i) => (
                    <div key={i} className="p-4 bg-gradient-to-br from-[#00b0ab]/5 to-transparent rounded-xl border">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-5 w-5 text-[#00b0ab]" />
                        <span className="font-bold">{m.milestone}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Week {m.projected_week} • {new Date(m.projected_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                      <ConfidenceBadge level={m.confidence} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Weekly Forecast Tab */}
        <TabsContent value="weekly" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Week-by-Week Predictions</CardTitle>
              <CardDescription>Detailed forecast for each week in the projection period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Week</th>
                      <th className="text-left py-3 px-4 font-medium">Date</th>
                      <th className="text-right py-3 px-4 font-medium">Projected ADC</th>
                      <th className="text-right py-3 px-4 font-medium">Range</th>
                      <th className="text-right py-3 px-4 font-medium">Admissions</th>
                      <th className="text-right py-3 px-4 font-medium">Discharges</th>
                      <th className="text-right py-3 px-4 font-medium">Net Change</th>
                      <th className="text-center py-3 px-4 font-medium">Confidence</th>
                      <th className="text-center py-3 px-4 font-medium">Seasonality</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecast?.weekly_forecasts?.map((week, i) => (
                      <tr key={i} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4 font-medium">Week {week.week}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {new Date(week.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-[#00b0ab]">{week.projected_adc}</td>
                        <td className="py-3 px-4 text-right text-muted-foreground text-xs">
                          {week.adc_range.low} - {week.adc_range.high}
                        </td>
                        <td className="py-3 px-4 text-right text-emerald-600">+{week.projected_admissions}</td>
                        <td className="py-3 px-4 text-right text-rose-600">-{week.projected_discharges}</td>
                        <td className={`py-3 px-4 text-right font-medium ${week.net_change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {week.net_change >= 0 ? '+' : ''}{week.net_change}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <ConfidenceBadge level={week.confidence} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline">{week.seasonality_factor}x</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Scenarios Tab */}
        <TabsContent value="scenarios" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Scenario Cards */}
            <div className="space-y-4">
              {forecast?.scenarios && Object.entries(forecast.scenarios).map(([key, scenario]) => (
                <Card key={key} className={`border-l-4 ${
                  key === 'optimistic' ? 'border-l-emerald-500' :
                  key === 'pessimistic' ? 'border-l-rose-500' : 'border-l-[#00b0ab]'
                }`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{scenario.label}</h3>
                        <p className="text-sm text-muted-foreground">{scenario.probability}% probability</p>
                      </div>
                      <div className={`text-3xl font-bold ${
                        key === 'optimistic' ? 'text-emerald-600' :
                        key === 'pessimistic' ? 'text-rose-600' : 'text-[#00b0ab]'
                      }`}>
                        {scenario.end_adc}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Assumptions</p>
                      {scenario.assumptions.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          {a}
                        </div>
                      ))}
                    </div>
                    <Progress 
                      value={scenario.probability} 
                      className="mt-4 h-2"
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Scenario Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Scenario Comparison</CardTitle>
                <CardDescription>End-of-period ADC by scenario</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={scenarioData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [value, name === 'adc' ? 'Projected ADC' : name]}
                      />
                      <Bar 
                        dataKey="adc" 
                        fill="#00b0ab"
                        radius={[0, 4, 4, 0]}
                        label={{ position: 'right', fontSize: 12, fontWeight: 'bold' }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Staffing Tab */}
        <TabsContent value="staffing" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              title="Current Staff"
              value={forecast?.staffing_implications?.current_staff || 0}
              subtitle="RNCMs on team"
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Max Staff Needed"
              value={forecast?.staffing_implications?.max_staff_needed || 0}
              subtitle="Based on 15:1 caseload"
              icon={UserPlus}
              color="primary"
            />
            <StatCard
              title="Net Hiring Needed"
              value={forecast?.staffing_implications?.net_hiring_needed || 0}
              subtitle="To meet projected census"
              icon={Building2}
              color={forecast?.staffing_implications?.net_hiring_needed > 0 ? 'amber' : 'emerald'}
            />
          </div>
          
          {/* Hiring Timeline */}
          {forecast?.staffing_implications?.hiring_timeline?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#00b0ab]" />
                  Recommended Hiring Timeline
                </CardTitle>
                <CardDescription>When to hire additional staff based on projected census</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {forecast.staffing_implications.hiring_timeline.map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                        <UserPlus className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">Hire {item.hire} RNCM{item.hire > 1 ? 's' : ''}</p>
                        <p className="text-sm text-muted-foreground">{item.reason}</p>
                      </div>
                      <Badge variant="outline">By Week {item.by_week}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Weekly Staffing Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Staffing Analysis</CardTitle>
              <CardDescription>Staffing requirements based on projected census</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Week</th>
                      <th className="text-right py-3 px-4 font-medium">Projected ADC</th>
                      <th className="text-right py-3 px-4 font-medium">Required Staff</th>
                      <th className="text-right py-3 px-4 font-medium">Current Staff</th>
                      <th className="text-right py-3 px-4 font-medium">Gap</th>
                      <th className="text-center py-3 px-4 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecast?.staffing_implications?.weekly_breakdown?.map((week, i) => (
                      <tr key={i} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4 font-medium">Week {week.week}</td>
                        <td className="py-3 px-4 text-right">{week.projected_adc}</td>
                        <td className="py-3 px-4 text-right font-medium">{week.required_staff}</td>
                        <td className="py-3 px-4 text-right">{week.current_staff}</td>
                        <td className={`py-3 px-4 text-right font-medium ${
                          week.gap > 0 ? 'text-rose-600' : week.gap < 0 ? 'text-emerald-600' : ''
                        }`}>
                          {week.gap > 0 ? '+' : ''}{week.gap}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={
                            week.status === 'adequate' ? 'bg-emerald-100 text-emerald-700' :
                            week.status === 'urgent' ? 'bg-rose-100 text-rose-700' :
                            'bg-amber-100 text-amber-700'
                          }>
                            {week.status === 'adequate' ? 'Adequate' :
                             week.status === 'urgent' ? 'Urgent' : 'Hiring Needed'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Agency Health Tab */}
        <TabsContent value="health" className="space-y-6">
          {healthData ? (
            <>
              {/* Health Score Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className={`col-span-1 ${
                  healthData.overall_health_score >= 80 ? 'border-emerald-200 bg-emerald-50/30' :
                  healthData.overall_health_score >= 60 ? 'border-amber-200 bg-amber-50/30' :
                  'border-rose-200 bg-rose-50/30'
                }`}>
                  <CardContent className="p-6 text-center">
                    <Heart className={`h-12 w-12 mx-auto mb-3 ${
                      healthData.overall_health_score >= 80 ? 'text-emerald-500' :
                      healthData.overall_health_score >= 60 ? 'text-amber-500' :
                      'text-rose-500'
                    }`} />
                    <p className="text-sm text-muted-foreground">Overall Health Score</p>
                    <p className="text-5xl font-bold mt-1">{healthData.overall_health_score}</p>
                    <Badge className={`mt-2 ${
                      healthData.health_status === 'Excellent' ? 'bg-emerald-100 text-emerald-700' :
                      healthData.health_status === 'Good' ? 'bg-blue-100 text-blue-700' :
                      healthData.health_status === 'Fair' ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }`}>
                      {healthData.health_status}
                    </Badge>
                  </CardContent>
                </Card>
                
                {/* Domain Scores */}
                <Card className="col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-violet-500" />
                      Health Domain Scores
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(healthData.scores || {}).map(([domain, score]) => (
                      <div key={domain}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm font-medium capitalize">{domain.replace('_', ' ')}</span>
                          <span className="text-sm font-medium">{score}%</span>
                        </div>
                        <Progress 
                          value={score} 
                          className={`h-2 ${
                            score >= 80 ? '[&>div]:bg-emerald-500' :
                            score >= 60 ? '[&>div]:bg-amber-500' :
                            '[&>div]:bg-rose-500'
                          }`}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
              
              {/* Risk Summaries */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Staffing Risk */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Staffing Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge className={`mb-3 ${
                      healthData.staffing_summary?.risk_level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                      healthData.staffing_summary?.risk_level === 'CRITICAL' ? 'bg-rose-100 text-rose-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {healthData.staffing_summary?.risk_level || 'Unknown'}
                    </Badge>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Critical Gaps</span>
                        <span className="font-medium">{healthData.staffing_summary?.critical_gaps || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Visit Capacity</span>
                        <span className="font-medium capitalize">{healthData.staffing_summary?.visit_capacity || 'Unknown'}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Burnout Risk */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Burnout Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-2">
                      {healthData.burnout_summary?.at_risk_staff || 0}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Staff at elevated risk</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Caseload</span>
                        <span className="font-medium">{healthData.burnout_summary?.avg_caseload || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">High Caseload</span>
                        <span className="font-medium">{healthData.burnout_summary?.high_caseload_count || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Compliance */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Compliance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-2">
                      {healthData.compliance_summary?.overall_rate || 100}%
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">Overall compliance rate</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Critical Issues</span>
                        <span className={`font-medium ${
                          (healthData.compliance_summary?.critical_issues || 0) > 0 ? 'text-rose-600' : 'text-emerald-600'
                        }`}>
                          {healthData.compliance_summary?.critical_issues || 0}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Action Items */}
              {healthData.action_items && healthData.action_items.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-500" />
                      Priority Action Items
                    </CardTitle>
                    <CardDescription>Actions to improve agency health and safety</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {healthData.action_items.map((item, idx) => (
                        <div 
                          key={idx}
                          className={`p-4 rounded-lg border-l-4 ${
                            item.priority === 'HIGH' ? 'border-l-rose-500 bg-rose-50/50' :
                            item.priority === 'MEDIUM' ? 'border-l-amber-500 bg-amber-50/50' :
                            'border-l-blue-500 bg-blue-50/50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Badge className={
                              item.priority === 'HIGH' ? 'bg-rose-100 text-rose-700' :
                              item.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            }>
                              {item.priority}
                            </Badge>
                            <div>
                              <h4 className="font-semibold">{item.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                              {item.actions && (
                                <ul className="mt-2 space-y-1">
                                  {(Array.isArray(item.actions) ? item.actions : []).slice(0, 3).map((action, i) => (
                                    <li key={i} className="text-sm flex items-center gap-2">
                                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                      {typeof action === 'string' ? action : action.title}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Strategic Recommendations */}
              {healthData.recommendations && healthData.recommendations.length > 0 && (
                <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-violet-500" />
                      Strategic Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {healthData.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
              <p className="text-muted-foreground">Unable to load agency health data</p>
              <Button onClick={() => fetchForecast(true)} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Methodology Note */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Prediction Methodology</p>
              <p>
                Forecasts are generated using linear regression on historical census data, adjusted for monthly seasonality patterns. 
                Confidence intervals widen for longer forecast horizons. Predictions are most accurate for 4-8 week periods. 
                Staffing calculations assume a 15:1 patient-to-RNCM caseload ratio.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
